import React, { useEffect, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import { bindActionCreators } from 'redux';
import { Box, Button, CircularProgress, Stack, Typography } from '@mui/material';
import { OPTION_MODULES, OPTION_CODE_ALLOWLIST, moduleCodes, optionCodeLabel } from '../../../constants/optionCodes';
import {
  apiErrorMessage,
  grantOptionPermission,
  listOptionPermissions,
  revokeOptionPermission,
} from '../../../helpers/optionApi';
import * as actionsToast from '../../../store/toast/actions';

const PermissoesOpcoesTab = ({ userId, messageError }) => {
  const [grants, setGrants] = useState([]);
  const [loading, setLoading] = useState(false);
  const [busyCodigo, setBusyCodigo] = useState(null);
  const reportError = useRef(messageError);
  reportError.current = messageError;

  useEffect(() => {
    if (!userId) return undefined;
    let cancel = false;
    setLoading(true);
    listOptionPermissions(userId)
      .then((list) => {
        if (!cancel) setGrants(list);
      })
      .catch((error) => {
        if (!cancel) {
          reportError.current({ message: apiErrorMessage(error, 'Não foi possível carregar as permissões.') });
        }
      })
      .finally(() => {
        if (!cancel) setLoading(false);
      });
    return () => {
      cancel = true;
    };
  }, [userId]);

  const granted = new Set(grants.map((item) => item.codigo));
  const extra = grants.filter(
    (item) => !OPTION_CODE_ALLOWLIST.some((code) => code.codigo === item.codigo),
  );

  const toggle = async (codigo, enable) => {
    setBusyCodigo(codigo);
    try {
      if (enable) await grantOptionPermission(userId, codigo);
      else await revokeOptionPermission(userId, codigo);
      const list = await listOptionPermissions(userId);
      setGrants(list);
    } catch (error) {
      messageError({ message: apiErrorMessage(error, 'Não foi possível atualizar a permissão.') });
    } finally {
      setBusyCodigo(null);
    }
  };

  if (!userId) {
    return (
      <Typography color="text.secondary">
        Salve o usuário para conceder permissões de opções.
      </Typography>
    );
  }

  return (
    <Stack spacing={2} sx={{ py: 1 }}>
      <Typography variant="body2" color="text.secondary">
        Marque os códigos que este usuário pode criar, editar e inativar.
      </Typography>
      {loading ? <CircularProgress size={22} /> : null}
      <Stack spacing={2}>
        {OPTION_MODULES.map((mod) => (
          <Stack key={mod.id} spacing={1}>
            <Typography variant="subtitle2">{mod.label}</Typography>
            {moduleCodes(mod).map((code) => {
              const on = granted.has(code.codigo);
              return (
                <Box
                  key={code.codigo}
                  sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 2 }}
                >
                  <Box>
                    <Typography variant="body2">{code.label}</Typography>
                    <Typography variant="caption" color="text.secondary">{code.codigo}</Typography>
                  </Box>
                  <Button
                    size="small"
                    variant={on ? 'contained' : 'outlined'}
                    disabled={busyCodigo === code.codigo}
                    onClick={() => toggle(code.codigo, !on)}
                  >
                    {on ? 'Concedido' : 'Conceder'}
                  </Button>
                </Box>
              );
            })}
          </Stack>
        ))}
      </Stack>
      {extra.length > 0 ? (
        <Stack spacing={1}>
          <Typography variant="body2" color="text.secondary">
            Concedidos fora da lista padrão. Dá para revogar.
          </Typography>
          {extra.map((item) => (
            <Box
              key={item.codigo}
              sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 2 }}
            >
              <Typography variant="body2">{optionCodeLabel(item.codigo)}</Typography>
              <Button
                size="small"
                variant="outlined"
                disabled={busyCodigo === item.codigo}
                onClick={() => toggle(item.codigo, false)}
              >
                Revogar
              </Button>
            </Box>
          ))}
        </Stack>
      ) : null}
    </Stack>
  );
};

PermissoesOpcoesTab.propTypes = {
  userId: PropTypes.string,
  messageError: PropTypes.func.isRequired,
};

PermissoesOpcoesTab.defaultProps = {
  userId: null,
};

const mapDispatchToProps = (dispatch) => bindActionCreators({ ...actionsToast }, dispatch);

export default connect(null, mapDispatchToProps)(PermissoesOpcoesTab);
